import { MessageCenter } from '@/services/socket/messageCenter'
import type { CoreWebSocket } from '@/services/socket/types'
import i18n from '@/i18n'
import * as Sentry from '@sentry/vue'

const t = (key: string): string => (i18n.global as any).t(key)

class SocketConnectionError extends Error {
  constructor(public readonly eventType: string) {
    super(`WebSocket connection error: ${eventType}`)
    this.name = 'SocketConnectionError'
  }
}

export class CoreSocket {
  private coreSocket: CoreWebSocket | null = null
  private refreshToken: string | null = null
  private isAlive: ReturnType<typeof setTimeout> | number = 0
  public messageCenter: MessageCenter | null = null

  constructor() {}

  onSocketClose = async (event: CloseEvent) => {
    Sentry.logger.info('Socket closed', { code: event.code, wasClean: event.wasClean })
    this.messageCenter?.close()
    clearInterval(this.isAlive)
    this.testReconnectCoreSocket()
  }

  onSocketError = (error: Event) => {
    if (import.meta.env.VITE_SENTRY_ENABLED !== 'true') {
      console.error('[Socket Error]', error.type)
    }
    Sentry.captureException(new SocketConnectionError(error.type), {
      fingerprint: ['socket', 'connection', error.type],
      tags: { socket_event: error.type },
    })
  }

  onSocketMessage = (event: MessageEvent) => {
    this.messageCenter?.handleMessage(event)
  }

  setRefreshToken(token: string | null) {
    this.refreshToken = token
  }

  async isConnected(socket: CoreWebSocket, timeout = 0): Promise<number> {
    return new Promise((resolve) =>
      setTimeout(async () => {
        if (socket.readyState === 0 && timeout < 5000) {
          resolve(await this.isConnected(socket, (timeout + 1) * 2))
        } else {
          // readyState 1=OPEN, 2=CLOSING and 3=CLOSED
          resolve(socket.readyState)
        }
      }, timeout)
    )
  }

  async isDataReady(socket: CoreWebSocket, timeout = 0): Promise<boolean> {
    return new Promise((resolve) => {
      setTimeout(async () => {
        if (socket.dataIsReady || timeout > 10000) {
          resolve(socket.dataIsReady)
        } else if (this.messageCenter?.getStatus() === 'error') {
          resolve(false)
        } else {
          this.messageCenter?.triggerMessage('client_is_ready', null)
          resolve(await this.isDataReady(socket, (timeout + 1) * 2))
        }
      }, timeout)
    })
  }

  testReconnectCoreSocket(timeout = 1000) {
    if (timeout > 10 * 60 * 1000 || !this.refreshToken) {
      // cancel trying to reconnect after ~ 20 minutes
      this.messageCenter?.hideDialog()
      this.messageCenter?.showErrorDialog(
        'refresh_failed',
        !this.refreshToken ? t('errors.api.refresh_failed.title') : t('errors.api.socket_lost.title')
      )
      return
    }
    setTimeout(() => {
      if (document.hidden) {
        /* window is in background - wait to show up againg */
        this.testReconnectCoreSocket()
      } else {
        this.reconnectCoreSocket(timeout)
      }
    }, timeout)
  }

  async reconnectCoreSocket(timeout = 1000) {
    const readyState: number = await this.initCoreSocket(this.refreshToken, true)
    if (readyState !== 1) {
      if (timeout === 1000) {
        // called only first time from testReconnectCoreSocket
        this.messageCenter?.hideDialog()
        this.messageCenter?.showErrorDialog('socket_lost', t('errors.api.socket_lost.message'))
      }
      this.testReconnectCoreSocket(2 * timeout)
    } else {
      Sentry.logger.info('Socket reconnected')
      return
    }
  }

  async initCoreSocket(jwt: string | null, reconnecting = false) {
    const url = import.meta.env.VITE_SOCKET_URL + '?token=' + jwt
    this.coreSocket = new WebSocket(url) as CoreWebSocket
    const readyState = await this.isConnected(this.coreSocket)
    this.messageCenter = new MessageCenter(this.coreSocket, {})
    this.coreSocket.onclose = this.onSocketClose
    this.coreSocket.onmessage = this.onSocketMessage
    this.coreSocket.onerror = this.onSocketError
    if (readyState === 1) {
      this.messageCenter.triggerMessage('client_is_ready', null)
      this.messageCenter.setStatus(reconnecting ? 'reconnected' : 'connected')
      this.messageCenter.hideDialog()
      this.messageCenter.startHeartbeat()
      this.checkingHeartBeat()
    }
    return readyState
  }

  async checkSocketDataReady() {
    if (!this.coreSocket) {
      return false
    }
    if (this.coreSocket?.dataIsReady) {
      return true
    }
    return await this.isDataReady(this.coreSocket)
  }

  checkingHeartBeat(timeout = 40 * 1000) {
    // default set to double ping timeout
    this.isAlive = setTimeout(async () => {
      if (!this.messageCenter?.isAlive || Date.now() - this.messageCenter.isAlive > timeout) {
        Sentry.logger.warn('Socket heartbeat dead', { status: this.messageCenter?.getStatus() })
        return this.coreSocket?.close() /* triggers onClose -> reconnection */
      }
      // if tab is in background slow down checkingHeartBeat -
      // interval gets slowed down by chrome & firefox to 1 per minute - handled
      // safari cancels intervals completely after around 15min - handled
      this.checkingHeartBeat(document.hidden ? 61 * 1000 : 40 * 1000)
    }, timeout)
  }
}

const socket: CoreSocket = new CoreSocket()
export const useCoreSocket = (): CoreSocket => socket
